import React, { useState, useEffect } from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import { useQueryParam, NumberParam } from "use-query-params"
import { motion } from "framer-motion"

import Metadata from "../components/Metadata"
import ExerciseThumbnail from "../components/ExerciseThumbnail"
import FooterPitch from "../components/FooterPitch"
import { Head, Stats, Stat, Data } from "./resource"
import Sean from "../images/sean_klein.jpg"

const ExercisesPage = ({
  data: {
    programme: { movements },
  },
  pageContext: { categories, equipment, selectedEquipment, selectedCategory },
  location,
}) => {
  // Pages
  const [paginatedExercises, setPaginatedExercises] = useState([])
  const [pageCount, setPageCount] = useState(null)
  const [page = 1, setPage] = useQueryParam("page", NumberParam)
  const [search, setSearch] = useState("")
  const [keyword, setKeyword] = useState(null)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const PAGE_SIZE = 24

  // Handle pagination
  useEffect(() => {
    let newExercises

    if (search) {
      newExercises = movements.filter(m =>
        m.name.toLowerCase().includes(search.toLowerCase())
      )
    } else {
      newExercises = movements
    }

    // update page count before filtering page
    setPageCount(Math.ceil(newExercises.length / PAGE_SIZE))

    // paginate movements
    newExercises = newExercises.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    setPaginatedExercises(newExercises)
  }, [movements, page, search])

  // Handle new search
  useEffect(() => {
    if (search) setPage(1)
  }, [search])

  const origin = location.origin || "https://programme.app"

  // Handle keyword
  useEffect(() => {
    if (selectedEquipment || selectedCategory) {
      setKeyword(
        `${selectedEquipment ? `${selectedEquipment} ` : ""}
       ${selectedCategory ? `${selectedCategory} ` : ""}
       Exercises`
      )
    } else {
      setKeyword(null)
    }
  }, [selectedCategory, selectedEquipment])

  return (
    <>
      <Metadata
        title={
          keyword
            ? `${movements.length || ""} ${keyword}`
            : "1000+ Exercises for coaches, educators & bloggers."
        }
        description={`Free-to-use ${
          keyword ? keyword + " " : ""
        }exercise videos for training, teaching and blogging. Embed into any website in two clicks.`}
        url={`${origin}${location.pathname}`}
      />
      {keyword ? (
        <Head padding withFilters>
          <h1>
            {movements.length || ""} {keyword}
          </h1>
          <div>
            <h2>Add {keyword.toLowerCase()} to your workouts</h2>
            <p>
              Get the benefits of high quality videos, instruction and
              guidelines to make sure you're performing exercises correctly.
            </p>
            <h2>Add {keyword.toLowerCase()} to your workflow</h2>
            <p>
              Use {keyword.toLowerCase()} to enhance your programming for your
              personal training clients or embed {keyword.toLowerCase()} into
              your blog or website and offer a great experience for your
              readers.
            </p>
            <Stats>
              <Stat>
                <img alt="Sean's Profile" src={Sean} />
                <Data>
                  <div>Compiled by</div>
                  <div>Sean Klein</div>
                </Data>
              </Stat>
              <Stat>
                <Data>
                  <div>Last Updated</div>
                  <div>01/03/22</div>
                </Data>
              </Stat>
            </Stats>
          </div>
        </Head>
      ) : (
        <Heading>
          <h1>
            <strong>1000+ Exercises. </strong>
            <br />
            For coaches, educators <br /> & bloggers.
          </h1>
        </Heading>
      )}
      <Content>
        <Sidebar>
          <Input
            placeholder="Search Exercises"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <FilterButton onClick={() => setIsFilterMenuOpen(true)}>
            Filters
          </FilterButton>
          <Filters
            variants={{
              open: {
                opacity: 1,
                pointerEvents: "auto",
              },
              closed: {
                opacity: 0,
                pointerEvents: "none",
              },
            }}
            transition={{ duration: 0.2 }}
            animate={isFilterMenuOpen ? "open" : "closed"}
            initial="closed"
          >
            <FiltersHeading>
              <Icon />
              <Title>Filters</Title>
              <Icon onClick={() => setIsFilterMenuOpen(curr => !curr)}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.999023 0.999023L13.0002 13.0002"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M0.999023 13.001L13.0002 0.999754"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </Icon>
            </FiltersHeading>
            <Filter>
              <a
                href={
                  selectedEquipment
                    ? origin +
                      `/exercises/equipment/${selectedEquipment
                        .replace(/ /g, "-")
                        .toLowerCase()}`
                    : origin + "/exercises"
                }
              >
                <FilterHeading>All Categories</FilterHeading>
              </a>
              {categories?.map(({ name, route, selected }) => (
                <a key={name} href={origin + route}>
                  <Option selected={selected}>{name}</Option>
                </a>
              ))}
            </Filter>
            <Filter>
              <a
                href={
                  selectedCategory
                    ? origin +
                      `/exercises/${selectedCategory
                        .replace(/ /g, "-")
                        .toLowerCase()}`
                    : origin + "/exercises"
                }
              >
                <FilterHeading>All Equipment</FilterHeading>
              </a>
              {equipment?.map(({ name, route, selected }) => (
                <a key={name} href={origin + route}>
                  <Option selected={selected}>{name}</Option>
                </a>
              ))}
            </Filter>
          </Filters>
        </Sidebar>
        <List>
          {paginatedExercises.map(m => (
            <ExerciseThumbnail key={m.id} {...m} />
          ))}
          <PageController>
            {page > 1 ? (
              <Page onClick={() => setPage(curr => curr - 1)}>
                {"<-"} Previous
              </Page>
            ) : null}
            {page > 5 ? (
              <span>
                <Page selected={pageCount === page} onClick={() => setPage(1)}>
                  {1}
                </Page>
                <Page>...</Page>
              </span>
            ) : null}

            {[...new Array(pageCount).keys()]
              .slice(page > 5 ? page - 4 : 0, page + 3)
              .map((i, idx) => (
                <Page
                  key={i}
                  selected={page === i + 1}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Page>
              ))}

            {pageCount - 3 > page ? (
              <span>
                <Page>...</Page>
                <Page
                  selected={pageCount === page}
                  onClick={() => setPage(pageCount)}
                >
                  {pageCount}
                </Page>
              </span>
            ) : null}
            {page < pageCount ? (
              <Page onClick={() => setPage(curr => (curr ? curr + 1 : 2))}>
                Next ->
              </Page>
            ) : null}
          </PageController>
        </List>
      </Content>
      <FooterPitch />
    </>
  )
}

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 14em 20px 2em;

  h1 {
    margin: 0;
    max-width: 50%;
    font-weight: normal;
    color: var(--light-grey);

    strong {
      color: var(--white);
      font-weight: normal;
    }
  }

  @media (max-width: 48em) {
    padding: 6em 20px 2em;
    text-align: center;
    h1 {
      max-width: 100%;
    }
  }
`

export const Content = styled.div`
  padding: 0px 20px;
  display: flex;
  flex-direction: row;

  @media (max-width: 48em) {
    padding: 0 12px;
    flex-direction: column;
  }
`

const Sidebar = styled.div`
  display: block;
`

const FilterButton = styled.div`
  border-radius: 40px;
  height: 40px;
  width: 100%;
  padding: 7px 15px;
  background: var(--dark-grey);
  border: 2px solid var(--dark-grey-offset);
  color: var(--white);
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: bold;
  display: none;

  @media (max-width: 48em) {
    display: block;
  }
`

export const Filters = styled(motion.div)`
  display: block;
  min-width: 220px;
  height: 100%;
  @media (min-width: 48em) {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
  @media (max-width: 48em) {
    height: 100vh;
    width: 100vw;
    overscroll-behavior: contain;
    z-index: 10000;
    max-height: 100vh;
    max-width: 100vw;
    overflow: scroll;
    position: fixed;
    top: 0px;
    left: 0px;
    padding: 15px;
    background: black;
  }
`

const FiltersHeading = styled.div`
  display: none;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 5px;

  @media (max-width: 48em) {
    display: flex;
  }
`

const Title = styled.div`
  color: white;
`

const Icon = styled.div`
  display: none;
  @media (max-width: 48em) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    z-index: 10000;
  }
`

export const Input = styled.input`
  border-radius: 40px;
  height: 40px;
  width: 100%;
  padding: 10px 15px;
  background: var(--dark-grey);
  border: 2px solid var(--dark-grey-offset);
  color: var(--white);
  margin-bottom: 24px;
  font-size: 16px;
  font-weight: bold;

  @media (max-width: 48em) {
    margin-bottom: 12px;
  }
`

export const Filter = styled.div`
  margin-bottom: 40px;
`

export const FilterHeading = styled.div`
  font-size: 16px;
  margin-left: 10px;
  font-weight: bold;
  color: #888;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: var(--white);
  }
`

export const Option = styled.div`
  color: var(--white);
  text-decoration: ${props => (props.selected ? "underline" : "none")};
  font-size: 16px;
  cursor: pointer;
  margin: 4px auto;
  margin-left: 8px;
  &:hover {
    text-decoration: underline;
  }
`

export const List = styled.div`
  display: flex;
  grid-gap: 8px;
  flex-wrap: wrap;
  padding-left: 12px;

  & > div,
  & > a {
    min-width: calc(50% - 4px);
    max-width: calc(50% - 4px);
  }

  @media (max-width: 64em) {
    grid-gap: 12px;
    & > div,
    & > a {
      min-width: 100%;
      max-width: 100%;
    }
  }

  @media (max-width: 48em) {
    padding: 0px;
  }
`

export const PageController = styled.div`
  clear: both;
  width: 100%;
  min-width: 100% !important;
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin: 20px auto 80px;
  grid-column: span 3;

  @media (max-width: 1320px) {
    grid-column: span 2;
  }

  @media (max-width: 48em) {
    grid-column: span 1;
  }
`

export const Page = styled.div`
  cursor: pointer;
  text-decoration: ${({ selected }) => (selected ? "underline" : "none")};
  margin: 10px;
  display: inline-block;
  font-size: 14px;
  color: white;
`

export const pageQuery = graphql`
  query ($target_id: ID, $type_id: ID, $category_id: ID, $equipment_id: ID) {
    programme {
      movements(
        target_id: $target_id
        type_id: $type_id
        category_id: $category_id
        equipment_id: $equipment_id
      ) {
        id
        name
        cues
        type
        category
        target
        bilateral
        level
        thumbnail_url
        video_url
        equipment {
          name
        }
        volume_bound_upper
        volume_bound_lower
        intensity_multiplier
      }
    }
  }
`

export default ExercisesPage
